import { verboseDebug } from 'classes/helpers/debug.helper';
import { AimingContext } from 'contexts/aiming.context';
import { HittersContext } from 'contexts/hitters.context';
import { MachineContext } from 'contexts/machine.context';
import { ResetPlateMode, USE_ABS_PITCH_BACKUP } from 'enums/machine.enums';
import useLatest from 'hooks/useLatestRef';
import { IVerboseComponent } from 'interfaces/i-components';
import { ISendBP } from 'interfaces/i-pitch-aiming';
import { useContext, useEffect } from 'react';
import useListLibraryStore from 'stores/use-list-library-store';

// automatically loads shots whenever the relevant shotIDs changes on the TMS record
const useSyncStrikeZoneListener = (props: IVerboseComponent) => {
  const { machine, lastMS, activeStrikeZone } = useContext(MachineContext);

  const latestPreview = useLatest(machine.enable_pitch_preview);
  const latestMsHash = useLatest(lastMS?.matching_hash);

  const { isTraining, pitchBP, setAndSendForBP } = useContext(AimingContext);
  const latestSSBP = useLatest(setAndSendForBP);
  const latestIsTraining = useLatest(isTraining);

  const { active: activeHitter } = useContext(HittersContext);
  const latestHitter = useLatest(activeHitter);

  useEffect(() => {
    if (!USE_ABS_PITCH_BACKUP) {
      // skip everything while disabled
      return;
    }

    const callback = async () => {
      if (latestIsTraining.current) {
        // skip everything while training
        return;
      }

      const bs_hash = pitchBP?._mx_tms?.bs_hash ?? '';

      if (!bs_hash) {
        // nothing is selected
        return;
      }

      const shouldAutoSend =
        latestMsHash.current &&
        latestMsHash.current === pitchBP?._mx_tms?.ms.matching_hash;

      if (!shouldAutoSend) {
        await latestSSBP.current({
          pitch: pitchBP,
          resetPlate: ResetPlateMode.PitchBackup, // * IMPORTANT for changing hitters to fix things based on the SAVED plate
          sendConfig: undefined,
        });

        verboseDebug(props, 'triggered setPitchBP w/o auto-send');
        return;
      }

      // resend the pitch using the latest shots
      const sendConfig: ISendBP = {
        skipFiringCheck: true,
        skipPreview: !latestPreview.current,
        list: useListLibraryStore.getState().activeList,
        hitter: latestHitter.current,
        trigger: `${props.identifier} > strike zone changed`,
      };

      await latestSSBP.current({
        pitch: pitchBP,
        resetPlate: ResetPlateMode.PitchBackup, // * IMPORTANT for changing hitters to fix things based on the SAVED plate
        sendConfig: sendConfig,
      });

      verboseDebug(props, 'triggered setPitchBP w/ auto-send', sendConfig);
    };

    callback();
  }, [pitchBP, activeStrikeZone]);
};

export default useSyncStrikeZoneListener;
